import { Fragment, useState, useEffect, useContext } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
    XMarkIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    TrashIcon,
    CalendarIcon,
    UserIcon,
    CreditCardIcon,
    CubeIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../../Common'
import { AuthContextApi } from '../../../Context/api'
import { toast } from 'react-toastify'

const ModalFatura = ({ states, setters, actionCreate, color = 'purple' }) => {
    const colorClasses = colorMap[color] || colorMap.purple
    const { getClientes, getContasRecebimento, getServicos } = useContext(AuthContextApi)

    // Estados principais
    const [dataFatura, setDataFatura] = useState('')
    const [clienteSelecionado, setClienteSelecionado] = useState(null)
    const [contaSelecionada, setContaSelecionada] = useState(null)
    const [servicosSelecionados, setServicosSelecionados] = useState([])

    // Estados de busca de cliente
    const [buscaCliente, setBuscaCliente] = useState('')
    const [tipoBuscaCliente, setTipoBuscaCliente] = useState('nome')
    const [clientesEncontrados, setClientesEncontrados] = useState([])
    const [loadingClientes, setLoadingClientes] = useState(false)
    const [mostrarResultadosCliente, setMostrarResultadosCliente] = useState(false)

    // Estados de busca de conta
    const [contasEncontradas, setContasEncontradas] = useState([])
    const [loadingContas, setLoadingContas] = useState(false)
    const [mostrarResultadosConta, setMostrarResultadosConta] = useState(false)

    // Estados de serviços
    const [servicosDisponiveis, setServicosDisponiveis] = useState([])
    const [loadingServicos, setLoadingServicos] = useState(false)
    const [paginaServicos, setPaginaServicos] = useState(0)

    // Estado de adição de serviço
    const [servicoParaAdicionar, setServicoParaAdicionar] = useState(null)
    const [dataInicial, setDataInicial] = useState('')
    const [dataFinal, setDataFinal] = useState('')
    const [quantidade, setQuantidade] = useState('1')
    const [precoServico, setPrecoServico] = useState('')
    const [observacaoServico, setObservacaoServico] = useState('')
    const [mostrarObservacaoServico, setMostrarObservacaoServico] = useState(false)

    // Estado de edição de observação
    const [editandoObservacao, setEditandoObservacao] = useState(null)
    const [observacaoEdicao, setObservacaoEdicao] = useState('')

    const [errors, setErrors] = useState({})

    // Carregar contas automaticamente quando cliente for selecionado
    useEffect(() => {
        if (clienteSelecionado) {
            carregarTodasContas()
        } else {
            setContasEncontradas([])
        }
    }, [clienteSelecionado])

    // Carregar serviços quando o modal abrir
    useEffect(() => {
        if (states.modalFatura) {
            carregarServicos()
        }
    }, [states.modalFatura, paginaServicos])

    const removerFormatacaoCnpj = (value) => value.replace(/\D/g, '')

    const formatarCnpj = (value) => {
        const cnpj = removerFormatacaoCnpj(value).slice(0, 14)
        if (cnpj.length <= 2) return cnpj
        if (cnpj.length <= 5) return cnpj.replace(/^(\d{2})(\d+)/, '$1.$2')
        if (cnpj.length <= 8) return cnpj.replace(/^(\d{2})(\d{3})(\d+)/, '$1.$2.$3')
        if (cnpj.length <= 12) return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4')
        return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d+)/, '$1.$2.$3/$4-$5')
    }

    const buscarClientes = async () => {
        const termoBusca = tipoBuscaCliente === 'cnpj'
            ? removerFormatacaoCnpj(buscaCliente)
            : buscaCliente.trim()

        if (termoBusca.length < 3) {
            toast.warning(
                tipoBuscaCliente === 'cnpj'
                    ? 'Digite pelo menos 3 numeros para buscar por CNPJ'
                    : 'Digite pelo menos 3 caracteres para buscar por nome'
            )
            return
        }

        setLoadingClientes(true)
        try {
            const params = new URLSearchParams({
                page: 0,
                per_page: 10,
                sort: 'nome_fantasia',
                sort_dir: 'asc',
                filter: tipoBuscaCliente === 'cnpj' ? 'cnpj' : 'nome_fantasia',
                search: tipoBuscaCliente === 'cnpj' ? termoBusca.replace('.', '').replace('-', '').replace('/', '') : termoBusca
            })
            const data = await getClientes(params.toString())
            setClientesEncontrados(data || [])
            setMostrarResultadosCliente(true)
        } catch (error) {
            toast.error('Erro ao buscar clientes')
        }
        setLoadingClientes(false)
    }

    const carregarTodasContas = async () => {
        setLoadingContas(true)
        try {
            const params = new URLSearchParams({
                page: 0,
                per_page: 100,
            })
            const data = await getContasRecebimento(params.toString())
            console.log(data)
            setContasEncontradas(data || [])
            setMostrarResultadosConta(true)
        } catch (error) {
            toast.error('Erro ao carregar contas')
        }
        setLoadingContas(false)
    }

    const handleKeyPressCliente = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            buscarClientes()
        }
    }

    const carregarServicos = async () => {
        setLoadingServicos(true)
        try {
            const params = new URLSearchParams({
                page: paginaServicos,
                per_page: 100,
                sort: 'nome',
                sort_dir: 'asc',
                filter: 'nome',
                search: ''
            })
            await getServicos(params.toString())
            const data = await getServicos(params.toString())
            console.log(data)
            setServicosDisponiveis(data || [])
        } catch (error) {
            toast.error('Erro ao carregar serviços')
        }
        setLoadingServicos(false)
    }

    const selecionarCliente = (cliente) => {
        setClienteSelecionado(cliente)
        setMostrarResultadosCliente(false)
        setBuscaCliente('')
        setClientesEncontrados([])
        setContaSelecionada(null)
        setContasEncontradas([])
    }

    const selecionarConta = (conta) => {
        setContaSelecionada(conta)
        setMostrarResultadosConta(false)
    }

    const selecionarServicoParaAdicionar = (servico) => {
        setServicoParaAdicionar(servico)
        setDataInicial('')
        setDataFinal('')
        setQuantidade('1')
        setPrecoServico('')
        setObservacaoServico('')
        setMostrarObservacaoServico(false)
    }

    const adicionarServico = () => {
        if (!servicoParaAdicionar) {
            toast.error('Selecione um serviço')
            return
        }
        if (!quantidade || quantidade <= 0) {
            toast.error('Informe a quantidade')
            return
        }
        if (!precoServico) {
            toast.error('Informe o valor do serviço')
            return
        }

        const novoServico = {
            id: servicoParaAdicionar.id,
            servico_nome: servicoParaAdicionar.nome,
            servico_descricao: servicoParaAdicionar.descricao,
            servico_codigo_servico: servicoParaAdicionar.codigo_servico,
            servico_municipal: servicoParaAdicionar.municipal,
            quantidade: parseInt(quantidade),
            preco: parseCurrency(precoServico)
        }

        // Adicionar datas apenas se foram informadas
        if (dataInicial && dataInicial.trim() !== '') {
            novoServico.data_inicial = dataInicial
        }
        if (dataFinal && dataFinal.trim() !== '') {
            novoServico.data_final = dataFinal
        }

        // Adicionar observação se existir
        if (observacaoServico && observacaoServico.trim() !== '') {
            novoServico.observacao = observacaoServico.trim()
        }

        setServicosSelecionados([...servicosSelecionados, novoServico])
        setServicoParaAdicionar(null)
        setDataInicial('')
        setDataFinal('')
        setQuantidade('1')
        setPrecoServico('')
        setObservacaoServico('')
        setMostrarObservacaoServico(false)
        toast.success('Serviço adicionado!')
    }

    const removerServico = (index) => {
        setServicosSelecionados(servicosSelecionados.filter((_, i) => i !== index))
        toast.info('Serviço removido')
    }

    const iniciarEdicaoObservacao = (index) => {
        const servico = servicosSelecionados[index]
        setEditandoObservacao(index)
        setObservacaoEdicao(servico.observacao || '')
    }

    const salvarEdicaoObservacao = (index) => {
        const novosServicos = [...servicosSelecionados]
        if (observacaoEdicao && observacaoEdicao.trim() !== '') {
            novosServicos[index].observacao = observacaoEdicao.trim()
        } else {
            delete novosServicos[index].observacao
        }
        setServicosSelecionados(novosServicos)
        setEditandoObservacao(null)
        setObservacaoEdicao('')
        toast.success('Observação atualizada!')
    }

    const cancelarEdicaoObservacao = () => {
        setEditandoObservacao(null)
        setObservacaoEdicao('')
    }

    const removerObservacaoServico = (index) => {
        const novosServicos = [...servicosSelecionados]
        delete novosServicos[index].observacao
        setServicosSelecionados(novosServicos)
        toast.info('Observação removida')
    }

    const formatCurrency = (value) => {
        const numericValue = value.replace(/\D/g, '')
        const formattedValue = (Number(numericValue) / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
        return formattedValue
    }

    const parseCurrency = (value) => {
        const numericValue = value.replace(/\D/g, '')
        return Math.round(Number(numericValue))
    }

    const calcularTotalFatura = () => {
        return servicosSelecionados.reduce((total, servico) => {
            return total + (servico.quantidade * servico.preco)
        }, 0)
    }

    const validate = () => {
        const newErrors = {}

        if (!dataFatura) {
            newErrors.dataFatura = 'Data da fatura é obrigatória'
        } else {
            const dataAtual = new Date().toISOString().split('T')[0]
            if (dataFatura > dataAtual) {
                newErrors.dataFatura = 'A data da fatura não pode ser maior que a data atual'
            }
        }

        if (!clienteSelecionado) newErrors.cliente = 'Selecione um cliente'
        if (!contaSelecionada) newErrors.conta = 'Selecione uma conta de recebimento'
        if (servicosSelecionados.length === 0) newErrors.servicos = 'Adicione pelo menos um serviço'

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) {
            toast.error('Preencha todos os campos obrigatórios')
            return
        }

        const dataToSend = {
            cliente_id: clienteSelecionado.id,
            dados_pagamento: contaSelecionada.id,
            data_fatura: dataFatura,
            total: calcularTotalFatura(),
            servicos: servicosSelecionados.map(s => {
                const servico = {
                    id: s.id,
                    quantidade: s.quantidade,
                    preco: s.preco
                }

                // Adicionar datas apenas se foram informadas
                if (s.data_inicial && s.data_inicial.trim() !== '') {
                    servico.data_inicial = s.data_inicial
                }
                if (s.data_final && s.data_final.trim() !== '') {
                    servico.data_final = s.data_final
                }

                // Adicionar observação se existir
                if (s.observacao && s.observacao.trim() !== '') {
                    servico.observacao = s.observacao.trim()
                }
                return servico
            })
        }

        await actionCreate(dataToSend)
    }

    const resetForm = () => {
        setDataFatura('')
        setClienteSelecionado(null)
        setContaSelecionada(null)
        setServicosSelecionados([])
        setBuscaCliente('')
        setTipoBuscaCliente('nome')
        setClientesEncontrados([])
        setContasEncontradas([])
        setServicoParaAdicionar(null)
        setDataInicial('')
        setDataFinal('')
        setQuantidade('1')
        setPrecoServico('')
        setObservacaoServico('')
        setMostrarObservacaoServico(false)
        setEditandoObservacao(null)
        setObservacaoEdicao('')
        setErrors({})
        setMostrarResultadosCliente(false)
        setMostrarResultadosConta(false)
    }

    useEffect(() => {
        if (!states.modalFatura) {
            resetForm()
        }
    }, [states.modalFatura])

    return (
        <Transition.Root show={states.modalFatura} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalFatura(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                                        onClick={() => setters.setModalFatura(false)}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="border-b border-gray-200 pb-4">
                                    <div className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg ${colorClasses.containerBg} mb-3`}>
                                        <span className="text-sm font-semibold text-gray-700">➕ Nova Fatura</span>
                                    </div>
                                    <h3 className={`text-2xl font-bold leading-7 text-center ${colorClasses.textGradient}`}>
                                        Cadastrar Fatura
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        Preencha os dados para criar uma nova fatura
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8 max-h-[70vh] overflow-y-auto pr-2">
                                    {/* Data da Fatura */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <CalendarIcon className="h-5 w-5 text-purple-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações da Fatura</h4>
                                        </div>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <label htmlFor="dataFatura" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Data <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    id="dataFatura"
                                                    value={dataFatura}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => {
                                                        setDataFatura(e.target.value)
                                                        if (errors.dataFatura) setErrors({ ...errors, dataFatura: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.dataFatura ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600'} sm:text-sm sm:leading-6 transition-all`}
                                                />
                                                {errors.dataFatura && <p className="mt-1 text-sm text-red-600">{errors.dataFatura}</p>}
                                            </div>
                                        </div>

                                        {/* Valor Total Calculado */}
                                        {servicosSelecionados.length > 0 && (
                                            <div className="mt-4 bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-purple-700">Valor Total da Fatura:</span>
                                                    <span className="text-2xl font-bold text-purple-900">
                                                        {formatCurrency(String(calcularTotalFatura()))}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-purple-600 mt-1">Calculado automaticamente com base nos serviços</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Seleção de Cliente */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <UserIcon className="h-5 w-5 text-purple-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Cliente</h4>
                                        </div>

                                        {!clienteSelecionado ? (
                                            <div className="relative">
                                                <label htmlFor="buscaCliente" className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                                    Buscar Cliente <span className="text-red-500">*</span>
                                                </label>
                                                <div className="mb-2 flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setTipoBuscaCliente('nome')
                                                            setBuscaCliente('')
                                                            setClientesEncontrados([])
                                                            setMostrarResultadosCliente(false)
                                                        }}
                                                        className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${tipoBuscaCliente === 'nome'
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                            }`}
                                                    >
                                                        Nome
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setTipoBuscaCliente('cnpj')
                                                            setBuscaCliente('')
                                                            setClientesEncontrados([])
                                                            setMostrarResultadosCliente(false)
                                                        }}
                                                        className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${tipoBuscaCliente === 'cnpj'
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                            }`}
                                                    >
                                                        CNPJ
                                                    </button>
                                                </div>
                                                <div className="relative flex gap-2">
                                                    <div className="relative flex-1">
                                                        <input
                                                            type="text"
                                                            id="buscaCliente"
                                                            value={buscaCliente}
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                setBuscaCliente(
                                                                    tipoBuscaCliente === 'cnpj'
                                                                        ? formatarCnpj(value)
                                                                        : value
                                                                )
                                                            }}
                                                            onKeyPress={handleKeyPressCliente}
                                                            placeholder={tipoBuscaCliente === 'cnpj'
                                                                ? 'Digite o CNPJ e pressione Enter...'
                                                                : 'Digite o nome empresarial/fantasia e pressione Enter...'}
                                                            className={`block w-full rounded-lg border-0 py-2.5 pl-10 pr-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.cliente ? 'ring-red-300' : 'ring-gray-300'} focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6`}
                                                        />
                                                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={buscarClientes}
                                                        className="inline-flex items-center px-4 py-2.5 rounded-lg bg-purple-600 text-white font-medium text-sm shadow-sm hover:bg-purple-700 transition-colors"
                                                    >
                                                        Buscar
                                                    </button>
                                                </div>
                                                {errors.cliente && <p className="mt-1 text-sm text-red-600">{errors.cliente}</p>}
                                                <p className="mt-1 text-xs text-gray-500">
                                                    {tipoBuscaCliente === 'cnpj'
                                                        ? 'Digite pelo menos 3 numeros do CNPJ e pressione Enter ou clique em Buscar'
                                                        : 'Digite pelo menos 3 caracteres do nome e pressione Enter ou clique em Buscar'}
                                                </p>

                                                {loadingClientes && (
                                                    <div className="mt-2 text-center">
                                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                                                    </div>
                                                )}

                                                {mostrarResultadosCliente && clientesEncontrados.length > 0 && (
                                                    <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                                                        {clientesEncontrados.map((cliente) => (
                                                            <button
                                                                key={cliente.id}
                                                                onClick={() => selecionarCliente(cliente)}
                                                                className="w-full text-left px-4 py-3 hover:bg-purple-50 border-b border-gray-100 last:border-0 transition-colors"
                                                            >
                                                                <p className="font-medium text-gray-900">{cliente.nome_empresarial}</p>
                                                                <p className="text-sm text-gray-500">{cliente.cnpj} - {cliente.municipio}/{cliente.uf}</p>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                {mostrarResultadosCliente && !loadingClientes && clientesEncontrados.length === 0 && (
                                                    <p className="mt-2 text-sm text-gray-500">Nenhum cliente encontrado</p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="font-semibold text-gray-900">{clienteSelecionado.nome_empresarial}</p>
                                                        <p className="text-sm text-gray-600 mt-1">{clienteSelecionado.cnpj}</p>
                                                        <p className="text-sm text-gray-600">{clienteSelecionado.municipio}/{clienteSelecionado.uf}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => setClienteSelecionado(null)}
                                                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                                                    >
                                                        Alterar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Seleção de Conta de Recebimento */}
                                    {clienteSelecionado && (
                                        <div className="border-b border-gray-200 pb-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <CreditCardIcon className="h-5 w-5 text-purple-600" />
                                                <h4 className="text-base font-semibold text-gray-900">Conta de Recebimento</h4>
                                            </div>

                                            {!contaSelecionada ? (
                                                <div className="relative">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                                        Selecione uma Conta <span className="text-red-500">*</span>
                                                    </label>
                                                    {errors.conta && <p className="mb-2 text-sm text-red-600">{errors.conta}</p>}

                                                    {loadingContas ? (
                                                        <div className="text-center py-8">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                                                            <p className="mt-2 text-sm text-gray-600">Carregando contas...</p>
                                                        </div>
                                                    ) : contasEncontradas.length > 0 ? (
                                                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                                                            {contasEncontradas.map((conta) => (
                                                                <button
                                                                    key={conta.id}
                                                                    onClick={() => selecionarConta(conta)}
                                                                    className="w-full text-left px-4 py-3 hover:bg-purple-50 border-b border-gray-100 last:border-0 transition-colors"
                                                                >
                                                                    <p className="font-medium text-gray-900">{conta.nome_titular}</p>
                                                                    <p className="text-sm text-gray-500">PIX: {conta.chave_pix}</p>
                                                                    {conta.banco && (
                                                                        <p className="text-sm text-gray-500">{conta.banco} - Ag: {conta.agencia} - Conta: {conta.conta}</p>
                                                                    )}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-center text-gray-500 py-4">Nenhuma conta disponível</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="font-semibold text-gray-900">{contaSelecionada.nome_titular}</p>
                                                            <p className="text-sm text-gray-600 mt-1">PIX: {contaSelecionada.chave_pix}</p>
                                                            {contaSelecionada.banco && (
                                                                <p className="text-sm text-gray-600">
                                                                    {contaSelecionada.banco} - Ag: {contaSelecionada.agencia} - Conta: {contaSelecionada.conta}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setContaSelecionada(null)}
                                                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                                                        >
                                                            Alterar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Serviços */}
                                    {contaSelecionada && (
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <CubeIcon className="h-5 w-5 text-purple-600" />
                                                <h4 className="text-base font-semibold text-gray-900">Serviços</h4>
                                            </div>

                                            {/* Serviços já adicionados */}
                                            {servicosSelecionados.length > 0 && (
                                                <div className="mb-6">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-3">Serviços Adicionados ({servicosSelecionados.length})</h5>
                                                    <div className="space-y-2">
                                                        {servicosSelecionados.map((servico, index) => (
                                                            <div key={`${servico.id}-${index}`} className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{servico.servico_nome}</p>
                                                                        <p className="text-sm text-gray-600 mt-1">{servico.servico_descricao}</p>
                                                                        <p className="text-sm text-gray-700 mt-2">
                                                                            <strong>Código:</strong> {servico.servico_codigo_servico}
                                                                        </p>
                                                                        {/* {(servico.data_inicial || servico.data_final) && (
                                                                            <p className="text-sm text-gray-700 mt-1">
                                                                                <strong>Período:</strong> {servico.data_inicial ? formatDate(servico.data_inicial) : '-'} até {servico.data_final ? formatDate(servico.data_final) : '-'}
                                                                            </p>
                                                                        )} */}
                                                                        <div className="mt-2 flex gap-4">
                                                                            <p className="text-sm text-gray-700">
                                                                                <strong>Quantidade:</strong> {servico.quantidade}
                                                                            </p>
                                                                            <p className="text-sm text-gray-700">
                                                                                <strong>Valor Unit.:</strong> {formatCurrency(String(servico.preco))}
                                                                            </p>
                                                                            <p className="text-sm font-semibold text-purple-700">
                                                                                <strong>Subtotal:</strong> {formatCurrency(String(servico.quantidade * servico.preco))}
                                                                            </p>
                                                                        </div>

                                                                        {/* Exibição ou edição da observação */}
                                                                        {editandoObservacao === index ? (
                                                                            <div className="mt-3 bg-white border border-purple-300 rounded-md p-3">
                                                                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                                                                    Editar Observação
                                                                                </label>
                                                                                <textarea
                                                                                    rows={3}
                                                                                    value={observacaoEdicao}
                                                                                    onChange={(e) => setObservacaoEdicao(e.target.value)}
                                                                                    placeholder="Digite a observação..."
                                                                                    className="block w-full rounded-md border-0 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 resize-none"
                                                                                />
                                                                                <p className="text-xs text-gray-500 mt-1 mb-2">
                                                                                    {observacaoEdicao.length} caracteres
                                                                                </p>
                                                                                <div className="flex gap-2">
                                                                                    <button
                                                                                        onClick={() => salvarEdicaoObservacao(index)}
                                                                                        className="flex-1 inline-flex justify-center items-center px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors"
                                                                                    >
                                                                                        Salvar
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={cancelarEdicaoObservacao}
                                                                                        className="flex-1 inline-flex justify-center items-center px-3 py-1.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-300 transition-colors"
                                                                                    >
                                                                                        Cancelar
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                {servico.observacao ? (
                                                                                    <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                                                                        <div className="flex justify-between items-start mb-1">
                                                                                            <p className="text-xs font-medium text-yellow-800">Observação:</p>
                                                                                            <div className="flex gap-1">
                                                                                                <button
                                                                                                    onClick={() => iniciarEdicaoObservacao(index)}
                                                                                                    className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                                                                                                    title="Editar observação"
                                                                                                >
                                                                                                    Editar
                                                                                                </button>
                                                                                                <span className="text-gray-400">|</span>
                                                                                                <button
                                                                                                    onClick={() => removerObservacaoServico(index)}
                                                                                                    className="text-red-600 hover:text-red-700 text-xs font-medium"
                                                                                                    title="Remover observação"
                                                                                                >
                                                                                                    Remover
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                        <p className="text-xs text-yellow-900 whitespace-pre-wrap">{servico.observacao}</p>
                                                                                    </div>
                                                                                ) : (
                                                                                    <button
                                                                                        onClick={() => iniciarEdicaoObservacao(index)}
                                                                                        className="mt-2 inline-flex items-center px-2 py-1 rounded-md bg-purple-100 text-purple-700 font-medium text-xs hover:bg-purple-200 transition-colors"
                                                                                    >
                                                                                        <PlusIcon className="w-3 h-3 mr-1" />
                                                                                        Adicionar Observação
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => removerServico(index)}
                                                                        className="ml-4 text-red-600 hover:text-red-700 p-2"
                                                                        title="Remover serviço"
                                                                    >
                                                                        <TrashIcon className="w-5 h-5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {errors.servicos && servicosSelecionados.length === 0 && (
                                                <p className="mb-3 text-sm text-red-600">{errors.servicos}</p>
                                            )}

                                            {/* Adicionar novo serviço */}
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <h5 className="text-sm font-medium text-gray-700 mb-3">Adicionar Serviço</h5>

                                                {loadingServicos ? (
                                                    <div className="text-center py-4">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                                                        <p className="mt-2 text-sm text-gray-600">Carregando serviços...</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {!servicoParaAdicionar ? (
                                                            <div className="max-h-60 overflow-y-auto space-y-2">
                                                                {servicosDisponiveis.map((servico) => (
                                                                    <button
                                                                        key={servico.id}
                                                                        onClick={() => selecionarServicoParaAdicionar(servico)}
                                                                        className="w-full text-left px-4 py-3 bg-white hover:bg-purple-50 border border-gray-200 rounded-lg transition-colors"
                                                                    >
                                                                        <p className="font-medium text-gray-900">{servico.nome}</p>
                                                                        <p className="text-sm text-gray-600 mt-1">{servico.descricao}</p>
                                                                        <p className="mt-2 text-sm text-gray-500">
                                                                            Código: {servico.codigo_servico}
                                                                        </p>
                                                                    </button>
                                                                ))}

                                                                {servicosDisponiveis.length === 0 && (
                                                                    <p className="text-center text-gray-500 py-4">
                                                                        Nenhum serviço disponível
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-4">
                                                                <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                                                                    <div className="flex justify-between items-start mb-4">
                                                                        <div>
                                                                            <p className="font-semibold text-gray-900">{servicoParaAdicionar.nome}</p>
                                                                            <p className="text-sm text-gray-600 mt-1">{servicoParaAdicionar.descricao}</p>
                                                                            <p className="text-sm text-gray-700 mt-2">
                                                                                <strong>Código:</strong> {servicoParaAdicionar.codigo_servico}
                                                                            </p>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => setServicoParaAdicionar(null)}
                                                                            className="text-gray-400 hover:text-gray-600"
                                                                        >
                                                                            <XMarkIcon className="w-5 h-5" />
                                                                        </button>
                                                                    </div>

                                                                    {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Data Inicial <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-1">Opcional</span>
                                                                            </label>
                                                                            <input
                                                                                type="date"
                                                                                value={dataInicial}
                                                                                onChange={(e) => setDataInicial(e.target.value)}
                                                                                className="block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 sm:text-sm"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Data Final <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-1">Opcional</span>
                                                                            </label>
                                                                            <input
                                                                                type="date"
                                                                                value={dataFinal}
                                                                                onChange={(e) => setDataFinal(e.target.value)}
                                                                                className="block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 sm:text-sm"
                                                                            />
                                                                        </div>
                                                                    </div> */}

                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Quantidade <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                value={quantidade}
                                                                                onChange={(e) => setQuantidade(e.target.value)}
                                                                                placeholder="1"
                                                                                className="block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 sm:text-sm"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Valor Unitário <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={precoServico}
                                                                                onChange={(e) => setPrecoServico(formatCurrency(e.target.value))}
                                                                                placeholder="R$ 0,00"
                                                                                className="block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 sm:text-sm"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* Mostrar subtotal */}
                                                                    {quantidade && precoServico && (
                                                                        <div className="mt-3 bg-purple-50 rounded-lg p-3 border border-purple-200">
                                                                            <div className="flex justify-between items-center">
                                                                                <span className="text-sm font-medium text-purple-700">Subtotal do Serviço:</span>
                                                                                <span className="text-lg font-bold text-purple-900">
                                                                                    {formatCurrency(String(parseInt(quantidade || 0) * parseCurrency(precoServico)))}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Observação do Serviço (Opcional) */}
                                                                    <div className="mt-4">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <label className="block text-sm font-medium text-gray-700">
                                                                                    Observação
                                                                                </label>
                                                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Opcional</span>
                                                                            </div>
                                                                            {!mostrarObservacaoServico && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setMostrarObservacaoServico(true)}
                                                                                    className="inline-flex items-center px-2 py-1 rounded-md bg-purple-100 text-purple-700 font-medium text-xs hover:bg-purple-200 transition-colors"
                                                                                >
                                                                                    <PlusIcon className="w-3 h-3 mr-1" />
                                                                                    Adicionar
                                                                                </button>
                                                                            )}
                                                                        </div>

                                                                        {mostrarObservacaoServico && (
                                                                            <div className="space-y-2">
                                                                                <div className="flex justify-between items-center">
                                                                                    <p className="text-xs text-gray-500">
                                                                                        Digite observações específicas sobre este serviço
                                                                                    </p>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setMostrarObservacaoServico(false)
                                                                                            setObservacaoServico('')
                                                                                        }}
                                                                                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                                                                                    >
                                                                                        Remover
                                                                                    </button>
                                                                                </div>
                                                                                <textarea
                                                                                    rows={3}
                                                                                    value={observacaoServico}
                                                                                    onChange={(e) => setObservacaoServico(e.target.value)}
                                                                                    placeholder="Ex: Serviço com valor promocional do mês..."
                                                                                    className="block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-purple-600 text-sm resize-none"
                                                                                />
                                                                                <p className="text-xs text-gray-500">
                                                                                    {observacaoServico.length} caracteres
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <button
                                                                        onClick={adicionarServico}
                                                                        className="mt-4 w-full inline-flex justify-center items-center rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 transition-colors"
                                                                    >
                                                                        <PlusIcon className="w-5 h-5 mr-2" />
                                                                        Adicionar à Fatura
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalFatura(false)}
                                        className="inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={states.isLoading}
                                        className={`inline-flex justify-center rounded-lg ${colorClasses.headerButtonBg} px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonHover} transition-colors disabled:opacity-50`}
                                    >
                                        {states.isLoading ? 'Salvando...' : 'Criar Fatura'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalFatura

